import React, { useState, useEffect } from 'react';
import './App.css';
import CatalogForm from './components/CatalogForm';
import CatalogView from './components/CatalogView';
import ExportarSite from './components/ExportarSite';
import Configuracoes from './components/Configuracoes';
import BlingImport from './components/BlingImport';

function App() {
  const [view, setView] = useState('catalog');
  const [products, setProducts] = useState([]);
  const [showCfg, setShowCfg] = useState(false);
  const [showBling, setShowBling] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  // Carrega produtos: Cloudflare é a fonte da verdade
  // Se há alterações locais não publicadas (isDirty persistido), usa localStorage
  useEffect(() => {
    const wUrl = (localStorage.getItem('workerUrl') || '').replace(/\/$/, '');
    const aToken = localStorage.getItem('authToken') || '';

    const loadLocal = () => {
      const saved = localStorage.getItem('products');
      if (saved) setProducts(JSON.parse(saved));
    };

    if (wUrl && aToken) {
      // Se tinha alterações não publicadas antes de fechar, mantém o rascunho local
      const temRascunho = localStorage.getItem('isDirty') === 'true';
      if (temRascunho) {
        loadLocal();
        setIsDirty(true);
        return;
      }

      // Sem rascunho: busca do Cloudflare (fonte da verdade)
      fetch(`${wUrl}/catalog`, { headers: { 'X-Auth-Token': aToken } })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (Array.isArray(data) && data.length > 0) {
            setProducts(data);
            localStorage.setItem('products', JSON.stringify(data));
          } else {
            loadLocal();
          }
        })
        .catch(loadLocal);
    } else {
      loadLocal();
    }
  }, []);

  // Avisa ao fechar aba/janela se houver alterações não salvas
  useEffect(() => {
    const handler = (e) => {
      if (!isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  // Salva no localStorage e marca como não publicado (persiste entre sessões)
  const saveLocal = (newProducts) => {
    localStorage.setItem('products', JSON.stringify(newProducts));
    localStorage.setItem('isDirty', 'true');
    setIsDirty(true);
  };

  const handleAddProduct = (product) => {
    let newProducts;
    if (product.id) {
      newProducts = products.map(p => p.id === product.id ? product : p);
    } else {
      newProducts = [...products, { ...product, id: Date.now() }];
    }
    setProducts(newProducts);
    saveLocal(newProducts);
    setEditProduct(null);
    setView('catalog');
  };

  const handleDeleteProduct = (id) => {
    const newProducts = products.filter(p => p.id !== id);
    setProducts(newProducts);
    saveLocal(newProducts);
  };

  const handleToggleVendido = (id) => {
    const newProducts = products.map(p => {
      if (p.id !== id) return p;
      if (p.vendido) return { ...p, vendido: false };
      const qty = parseInt(p.quantidade) || 0;
      if (qty > 1) return { ...p, quantidade: String(qty - 1) };
      if (qty === 1) return { ...p, quantidade: '0', vendido: true };
      return { ...p, vendido: true };
    });
    setProducts(newProducts);
    saveLocal(newProducts);
  };

  const handleImportarBling = (novos) => {
    const comIds = novos.map(p => ({ ...p, id: Date.now() + Math.random() }));
    const newProducts = [...products, ...comIds];
    setProducts(newProducts);
    saveLocal(newProducts);
  };

  const handleAtualizarBling = (atualizacoes) => {
    const newProducts = products.map(p => {
      const update = atualizacoes.find(u => u.id === p.id);
      if (!update) return p;
      const { id, ...campos } = update;
      return { ...p, ...campos };
    });
    setProducts(newProducts);
    saveLocal(newProducts);
  };

  const temBling = !!(localStorage.getItem('workerUrl') && localStorage.getItem('authToken'));

  return (
    <div className="App">
      {showCfg && (
        <Configuracoes
          onClose={() => setShowCfg(false)}
          onRestaurar={(lista) => { setProducts(lista); localStorage.removeItem('isDirty'); setIsDirty(false); }}
        />
      )}
      {showBling && (
        <BlingImport
          onClose={() => setShowBling(false)}
          onImportar={handleImportarBling}
          onAtualizar={handleAtualizarBling}
        />
      )}
      <header className="app-header">
        <h1>📦 Catálogo de Produtos</h1>
        <div className="header-buttons">
          <button
            className={`btn ${view === 'catalog' ? 'active' : ''}`}
            onClick={() => { setEditProduct(null); setView('catalog'); }}
          >
            Catálogo ({products.length})
          </button>
          <button
            className={`btn ${view === 'form' ? 'active' : ''}`}
            onClick={() => { setEditProduct(null); setView('form'); }}
          >
            + Novo Produto
          </button>
          {temBling && (
            <button className="btn-bling" onClick={() => setShowBling(true)} title="Importar produtos do Bling">
              📦 Importar Bling
            </button>
          )}
          <ExportarSite
            products={products}
            isDirty={isDirty}
            onPublicado={() => { localStorage.removeItem('isDirty'); setIsDirty(false); }}
          />
          <button className="btn-cfg" onClick={() => setShowCfg(true)} title="Configurações">⚙️</button>
        </div>
      </header>

      <main className="app-main">
        {view === 'form' ? (
          <CatalogForm onAddProduct={handleAddProduct} editProduct={editProduct} />
        ) : (
          <CatalogView
            products={products}
            onDelete={handleDeleteProduct}
            onToggleVendido={handleToggleVendido}
            onEdit={(product) => {
              setEditProduct(product);
              window.scrollTo(0, 0);
              setView('form');
            }}
          />
        )}
      </main>
    </div>
  );
}

export default App;
